import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import PostCard from '../components/PostCard'
import { Loader2, MessageSquare, EyeOff, Eye } from 'lucide-react'
import toast from 'react-hot-toast'

const MyPosts = () => {
  const { api } = useAuth()
  const [posts, setPosts] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [filter, setFilter] = useState('all') // 'all', 'anonymous', 'identified'

  useEffect(() => {
    fetchMyPosts()
  }, [])

  const fetchMyPosts = async () => {
    try {
      const response = await api.get('/posts/user/me')
      if (response.data.success) {
        setPosts(response.data.data.posts)
      }
    } catch (error) {
      toast.error('Failed to load your posts')
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = (postId) => {
    setPosts(prev => prev.filter(p => p._id !== postId))
  }

  const filteredPosts = posts.filter(post => {
    if (filter === 'anonymous') return post.isAnonymous
    if (filter === 'identified') return !post.isAnonymous
    return true
  })

  const stats = {
    total: posts.length,
    anonymous: posts.filter(p => p.isAnonymous).length,
    identified: posts.filter(p => !p.isAnonymous).length
  }

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">My Confessions</h1>
        <a href="/" className="btn-secondary text-sm">
          Back to Feed
        </a>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`p-4 rounded-lg border text-center transition-colors ${
            filter === 'all' 
              ? 'bg-primary-50 border-primary-200' 
              : 'bg-white border-gray-200 hover:bg-gray-50'
          }`}
        >
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
          <p className="text-sm text-gray-500">Total</p>
        </button>
        <button
          onClick={() => setFilter('anonymous')}
          className={`p-4 rounded-lg border text-center transition-colors ${
            filter === 'anonymous' 
              ? 'bg-anonymous-50 border-anonymous-200' 
              : 'bg-white border-gray-200 hover:bg-gray-50'
          }`}
        >
          <div className="flex items-center justify-center space-x-1">
            <EyeOff className="w-4 h-4 text-anonymous-600" />
            <p className="text-2xl font-bold text-anonymous-700">{stats.anonymous}</p>
          </div>
          <p className="text-sm text-gray-500">Anonymous</p>
        </button>
        <button
          onClick={() => setFilter('identified')}
          className={`p-4 rounded-lg border text-center transition-colors ${
            filter === 'identified' 
              ? 'bg-blue-50 border-blue-200' 
              : 'bg-white border-gray-200 hover:bg-gray-50'
          }`}
        >
          <div className="flex items-center justify-center space-x-1">
            <Eye className="w-4 h-4 text-blue-600" />
            <p className="text-2xl font-bold text-blue-700">{stats.identified}</p>
          </div>
          <p className="text-sm text-gray-500">Identified</p>
        </button>
      </div>

      {/* Filter indicator */}
      <div className="mb-4">
        <p className="text-sm text-gray-500">
          Showing {filter === 'all' ? 'all' : filter} confessions 
          ({filteredPosts.length})
        </p>
      </div>

      {/* Posts list */}
      <div className="space-y-4">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {filter === 'all' 
                ? "You haven't posted any confessions yet"
                : `No ${filter} confessions yet`}
            </h3>
            <p className="text-gray-500 mb-4">
              {filter === 'all' 
                ? 'Share your first confession with the community'
                : 'Try a different filter or create a new confession'}
            </p>
            <a href="/" className="btn-primary">
              Create Confession
            </a>
          </div>
        ) : (
          filteredPosts.map((post) => (
            <PostCard 
              key={post._id} 
              post={post}
              onDelete={handleDelete}
            />
          ))
        )}
      </div>
    </div>
  )
}

export default MyPosts